/**
 * StoreManager - Admin component for managing the integrated store
 * Handles products, categories, orders, and customers
 * Now renders as a full-screen dialog for better usability
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package, Tag, Search, Plus, Edit2, Trash2, Image, DollarSign,
  CheckCircle, AlertCircle, Loader2, RefreshCw, Archive, Eye, EyeOff,
  Layers, ShoppingBag, Upload, Download, Filter, MoreHorizontal,
  ChevronDown, ChevronUp, Box, FolderOpen, Users, X, ArrowLeft,
  Truck, ClipboardList, PackageCheck, Send, ShoppingCart, FileText, Settings
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const API = process.env.REACT_APP_BACKEND_URL;

const StoreManager = ({ open, onOpenChange }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('products');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ products: {}, categories: 0, orders: {} });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Product editing state
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  
  // Category editing state
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Import status
  const [importStatus, setImportStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Fulfillment/Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [orderSearch, setOrderSearch] = useState('');

  // Customer orders view state
  const [viewingCustomerOrders, setViewingCustomerOrders] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailDialog, setShowOrderDetailDialog] = useState(false);

  // Tracking number dialog state
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [pendingShipOrder, setPendingShipOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');

  // Tax settings state
  const [taxRate, setTaxRate] = useState(8.265);
  const [savingTaxRate, setSavingTaxRate] = useState(false);

  // Pages editing state
  const [pages, setPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [showPageEditor, setShowPageEditor] = useState(false);
  const [savingPage, setSavingPage] = useState(false);

  // Order status options for fulfillment
  const ORDER_STATUSES = [
    { value: 'ordered', label: 'Ordered', color: 'bg-blue-600', icon: ClipboardList },
    { value: 'filling', label: 'Filling', color: 'bg-yellow-600', icon: Package },
    { value: 'filled', label: 'Filled', color: 'bg-purple-600', icon: PackageCheck },
    { value: 'shipped', label: 'Shipped', color: 'bg-green-600', icon: Truck },
    { value: 'complete', label: 'Complete', color: 'bg-emerald-600', icon: CheckCircle },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-600', icon: X }
  ];

  // Fetch products - increased limit for better display
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', '50');  // Increased from 20 for full page view
      params.append('exclude_hidden_categories', 'false');  // Admin sees all
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category_id', categoryFilter);
      if (typeFilter) params.append('product_type', typeFilter);
      
      const response = await axios.get(`${API}/api/store/products?${params.toString()}`);
      setProducts(response.data.products || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, categoryFilter, typeFilter]);

  // Fetch categories (include hidden for admin)
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/categories?include_hidden=true`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/stats`);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch import status
  const fetchImportStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/import/status`);
      setImportStatus(response.data.status);
      if (response.data.in_progress) {
        setImporting(true);
      } else {
        setImporting(false);
      }
    } catch (error) {
      console.error('Failed to fetch import status:', error);
    }
  }, []);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const params = new URLSearchParams();
      if (customerSearch) params.append('search', customerSearch);
      const response = await axios.get(`${API}/api/store/admin/customers?${params.toString()}`);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  }, [customerSearch]);

  // Fetch orders for fulfillment
  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (orderStatusFilter) params.append('status', orderStatusFilter);
      if (orderSearch) params.append('search', orderSearch);
      const response = await axios.get(`${API}/api/store/admin/orders?${params.toString()}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      // If endpoint doesn't exist yet, use empty array
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [orderStatusFilter, orderSearch]);

  // Update order status
  const updateOrderStatus = async (orderId, newStatus, trackingInfo = null) => {
    // If changing to shipped and no tracking provided, show dialog
    if (newStatus === 'shipped' && !trackingInfo) {
      const order = orders.find(o => o.id === orderId) || customerOrders.find(o => o.id === orderId);
      setPendingShipOrder({ orderId, order });
      setTrackingNumber('');
      setTrackingCarrier('');
      setShowTrackingDialog(true);
      return;
    }
    
    try {
      const payload = {
        fulfillment_status: newStatus
      };
      
      // Add tracking info if provided
      if (trackingInfo) {
        payload.tracking_number = trackingInfo.tracking_number;
        payload.tracking_carrier = trackingInfo.tracking_carrier;
      }
      
      await axios.put(`${API}/api/store/admin/orders/${orderId}/status`, payload);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
      // Also refresh customer orders if viewing
      if (viewingCustomerOrders) {
        fetchCustomerOrders(viewingCustomerOrders.email);
      }
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    }
  };

  // Handle shipping with tracking
  const handleShipWithTracking = () => {
    if (!pendingShipOrder) return;
    
    updateOrderStatus(pendingShipOrder.orderId, 'shipped', {
      tracking_number: trackingNumber,
      tracking_carrier: trackingCarrier
    });
    
    setShowTrackingDialog(false);
    setPendingShipOrder(null);
    setTrackingNumber('');
    setTrackingCarrier('');
  };

  // Fetch orders for a specific customer
  const fetchCustomerOrders = async (customerEmail) => {
    setCustomerOrdersLoading(true);
    try {
      const response = await axios.get(`${API}/api/store/admin/orders?search=${encodeURIComponent(customerEmail)}`);
      setCustomerOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch customer orders:', error);
      setCustomerOrders([]);
    } finally {
      setCustomerOrdersLoading(false);
    }
  };

  // View customer orders
  const handleViewCustomerOrders = (customer) => {
    setViewingCustomerOrders(customer);
    fetchCustomerOrders(customer.email);
  };

  // View single order detail
  const handleViewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailDialog(true);
  };

  // Fetch tax rate
  const fetchTaxRate = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/settings/tax-rate`);
      setTaxRate(response.data.tax_rate);
    } catch (error) {
      console.error('Failed to fetch tax rate:', error);
    }
  }, []);

  // Save tax rate
  const saveTaxRate = async () => {
    setSavingTaxRate(true);
    try {
      await axios.put(`${API}/api/store/settings/tax-rate`, { tax_rate: taxRate });
      toast.success(`Tax rate updated to ${taxRate}%`);
    } catch (error) {
      console.error('Failed to save tax rate:', error);
      toast.error('Failed to save tax rate');
    } finally {
      setSavingTaxRate(false);
    }
  };

  // Fetch pages
  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/pages`);
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    }
  }, []);

  // Save page content
  const savePage = async () => {
    if (!editingPage) return;
    setSavingPage(true);
    try {
      await axios.put(`${API}/api/store/pages/${editingPage.slug}`, {
        title: editingPage.title,
        content: editingPage.content
      });
      toast.success(`${editingPage.title} saved successfully`);
      setShowPageEditor(false);
      fetchPages();
    } catch (error) {
      console.error('Failed to save page:', error);
      toast.error('Failed to save page');
    } finally {
      setSavingPage(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (open) {
      fetchProducts();
      fetchCategories();
      fetchStats();
      fetchImportStatus();
      fetchCustomers();
      fetchOrders();
      fetchTaxRate();
      fetchPages();
    }
  }, [open, fetchProducts, fetchCategories, fetchStats, fetchImportStatus, fetchCustomers, fetchOrders, fetchTaxRate, fetchPages]);

  // Refresh on filter changes
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, categoryFilter, typeFilter, open]);

  // Start WooCommerce import
  const handleStartImport = async () => {
    if (!window.confirm('Start WooCommerce import? This will import all published products and categories.')) {
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/api/store/import/woocommerce`);
      toast.success(response.data.message || 'Import started');
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/api/store/import/status`);
          setImportStatus(statusRes.data.status);
          
          if (!statusRes.data.in_progress) {
            clearInterval(pollInterval);
            setImporting(false);
            fetchProducts();
            fetchCategories();
            fetchStats();
            toast.success('Import completed!');
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 3000);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start import');
      setImporting(false);
    }
  };

  // Save product
  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    
    setSavingProduct(true);
    try {
      if (editingProduct.id) {
        // Update existing
        await axios.put(`${API}/api/store/products/${editingProduct.id}`, editingProduct);
        toast.success('Product updated');
      } else {
        // Create new
        await axios.post(`${API}/api/store/products`, editingProduct);
        toast.success('Product created');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Save product error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSavingProduct(false);
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/api/store/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Delete product error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  // Save category
  const handleSaveCategory = async () => {
    if (!editingCategory) return;
    
    setSavingCategory(true);
    try {
      if (editingCategory.id) {
        // Update existing
        await axios.put(`${API}/api/store/categories/${editingCategory.id}`, editingCategory);
        toast.success('Category updated');
      } else {
        // Create new
        await axios.post(`${API}/api/store/categories`, editingCategory);
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Save category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSavingCategory(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? Products will be unassigned.')) return;
    
    try {
      await axios.delete(`${API}/api/store/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchCategories();
      fetchProducts();
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Open new product dialog
  const openNewProduct = () => {
    setEditingProduct({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      sku: '',
      price: 0,
      compare_at_price: null,
      category_ids: [],
      tags: [],
      images: [],
      product_type: 'physical',
      status: 'active',
      track_inventory: false,
      stock_quantity: null,
      downloadable: false,
      download_files: []
    });
    setShowProductDialog(true);
  };

  // Open edit product dialog
  const openEditProduct = (product) => {
    setEditingProduct({ ...product });
    setShowProductDialog(true);
  };

  // Open new category dialog
  const openNewCategory = () => {
    setEditingCategory({
      name: '',
      slug: '',
      description: '',
      parent_id: null,
      image_url: '',
      display_order: 0
    });
    setShowCategoryDialog(true);
  };

  // Delete customer
  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? Their orders will be preserved.')) return;
    
    try {
      await axios.delete(`${API}/api/store/admin/customers/${customerId}`);
      toast.success('Customer deleted');
      fetchCustomers();
      fetchStats();
    } catch (error) {
      console.error('Delete customer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  // Save customer
  const handleSaveCustomer = async () => {
    if (!editingCustomer) return;
    
    setSavingCustomer(true);
    try {
      await axios.put(`${API}/api/store/admin/customers/${editingCustomer.id}`, editingCustomer);
      toast.success('Customer updated');
      setShowCustomerDialog(false);
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error('Save customer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSavingCustomer(false);
    }
  };

  // Open edit category dialog
  const openEditCategory = (category) => {
    setEditingCategory({ ...category });
    setShowCategoryDialog(true);
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-600';
      case 'draft': return 'bg-yellow-600';
      case 'archived': return 'bg-gray-600';
      default: return 'bg-slate-600';
    }
  };

  // Product type badge colors
  const getTypeColor = (type) => {
    switch (type) {
      case 'physical': return 'bg-blue-600';
      case 'digital': return 'bg-purple-600';
      case 'service': return 'bg-teal-600';
      default: return 'bg-slate-600';
    }
  };

  return (
    <>
      {/* Full-screen Store Manager Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="max-w-[95vw] w-full h-[90vh] max-h-[90vh] bg-slate-900 border-slate-700 text-white flex flex-col p-0 overflow-hidden"
          showCloseButton={false}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-6 w-6 text-orange-400" />
              <div>
                <h2 className="text-xl font-bold text-white">Store Manager</h2>
                <p className="text-sm text-slate-400">Manage products, categories, customers, and orders</p>
              </div>
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              size="icon"
              className="text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-3 px-6 py-4 flex-shrink-0 border-b border-slate-700">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <Package className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                <div className="text-xl font-bold text-white">{stats.products?.total || 0}</div>
                <div className="text-xs text-slate-400">Products</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <FolderOpen className="h-5 w-5 mx-auto text-orange-400 mb-1" />
                <div className="text-xl font-bold text-white">{stats.categories || 0}</div>
                <div className="text-xs text-slate-400">Categories</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <Users className="h-5 w-5 mx-auto text-purple-400 mb-1" />
                <div className="text-xl font-bold text-white">{customers.length}</div>
                <div className="text-xs text-slate-400">Customers</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <CheckCircle className="h-5 w-5 mx-auto text-green-400 mb-1" />
                <div className="text-xl font-bold text-white">{stats.products?.active || 0}</div>
                <div className="text-xs text-slate-400">Active</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <AlertCircle className="h-5 w-5 mx-auto text-red-400 mb-1" />
                <div className="text-xl font-bold text-white">{stats.products?.low_stock || 0}</div>
                <div className="text-xs text-slate-400">Low Stock</div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs - Main Content Area */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden px-6 pb-4">
            <TabsList className="grid w-full grid-cols-5 bg-slate-800/50 flex-shrink-0 mb-4">
              <TabsTrigger value="products" data-testid="store-tab-products" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Package className="h-4 w-4 mr-2" />
                Products
              </TabsTrigger>
              <TabsTrigger value="categories" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Layers className="h-4 w-4 mr-2" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="fulfillment" data-testid="store-tab-fulfillment" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Truck className="h-4 w-4 mr-2" />
                Fulfillment
              </TabsTrigger>
              <TabsTrigger value="customers" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Users className="h-4 w-4 mr-2" />
                Customers
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products" className="flex-1 min-h-0 flex flex-col data-[state=active]:flex">
              {/* Filters */}
              <div className="flex gap-2 mb-4 flex-shrink-0 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[130px] bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[130px] bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="digital">Digital</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={openNewProduct} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
                <Button onClick={fetchProducts} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Products List - Scrollable container - much taller */}
              <div className="flex-1 overflow-y-auto" data-testid="store-products-scroll-container" style={{ minHeight: '200px' }}>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No products found</p>
                    <p className="text-sm mt-1">Try adjusting your filters or import from WooCommerce</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {products.map((product) => (
                      <Card key={product.id} data-testid="store-product-row" className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/80 transition-colors">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            {/* Product Image */}
                            <div className="w-16 h-16 rounded bg-slate-700 flex-shrink-0 overflow-hidden">
                              {product.images?.[0]?.url ? (
                                <img 
                                  src={product.images[0].url} 
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="h-6 w-6 text-slate-500" />
                                </div>
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-medium text-white truncate">{product.name}</h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge className={`${getStatusColor(product.status)} text-white text-xs`}>
                                      {product.status}
                                    </Badge>
                                    <Badge className={`${getTypeColor(product.product_type)} text-white text-xs`}>
                                      {product.product_type}
                                    </Badge>
                                    {product.sku && (
                                      <span className="text-xs text-slate-400">SKU: {product.sku}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="font-bold text-lg text-green-400">${product.price?.toFixed(2)}</div>
                                  {product.compare_at_price && (
                                    <div className="text-xs text-slate-400 line-through">${product.compare_at_price?.toFixed(2)}</div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Categories */}
                              {product.category_ids?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {product.category_ids.slice(0, 3).map(catId => (
                                    <Badge key={catId} variant="outline" className="text-xs border-slate-600 text-slate-300">
                                      {getCategoryName(catId)}
                                    </Badge>
                                  ))}
                                  {product.category_ids.length > 3 && (
                                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                                      +{product.category_ids.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              {/* Variants indicator */}
                              {product.has_variants && product.variants?.length > 0 && (
                                <div className="text-xs text-slate-400 mt-1">
                                  {product.variants.length} variants
                                </div>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                onClick={() => openEditProduct(product)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteProduct(product.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4 flex-shrink-0">
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                    className="border-slate-600"
                  >
                    Previous
                  </Button>
                  <span className="text-slate-400 text-sm self-center">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                    className="border-slate-600"
                  >
                    Next
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="flex-1 min-h-0 flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h3 className="text-lg font-medium text-white">Categories ({categories.length})</h3>
                <Button onClick={openNewCategory} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto" style={{ minHeight: '200px' }}>
                {categories.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No categories found</p>
                    <p className="text-sm mt-1">Import from WooCommerce or create new categories</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {categories.map((category) => (
                      <Card key={category.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/80 transition-colors">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {category.image_url ? (
                                <img 
                                  src={category.image_url} 
                                  alt={category.name}
                                  className="w-10 h-10 rounded object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center">
                                  <FolderOpen className="h-5 w-5 text-slate-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-white truncate">{category.name}</h4>
                                  {category.is_hidden && (
                                    <Badge className="bg-slate-600 text-slate-300 text-xs flex items-center gap-1">
                                      <EyeOff className="h-3 w-3" />
                                      Hidden
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {category.product_count || 0} products • slug: {category.slug}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                onClick={() => openEditCategory(category)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteCategory(category.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Fulfillment Tab */}
            <TabsContent value="fulfillment" className="flex-1 min-h-0 flex flex-col" data-testid="store-fulfillment-tab">
              <div className="flex gap-4 mb-4 flex-shrink-0 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by order # or customer..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <Select value={orderStatusFilter || "all"} onValueChange={(v) => setOrderStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="all">All Status</SelectItem>
                    {ORDER_STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchOrders} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <RefreshCw className={`h-4 w-4 ${ordersLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Status Legend */}
              <div className="flex flex-wrap gap-2 mb-4 flex-shrink-0">
                {ORDER_STATUSES.map(status => {
                  const IconComponent = status.icon;
                  return (
                    <Badge key={status.value} className={`${status.color} text-white text-xs flex items-center gap-1`}>
                      <IconComponent className="h-3 w-3" />
                      {status.label}
                    </Badge>
                  );
                })}
              </div>

              <div className="flex-1 overflow-y-auto" style={{ minHeight: '200px' }}>
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No orders to fulfill</p>
                    <p className="text-sm mt-1">Orders will appear here when customers complete checkout</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => {
                      const currentStatus = ORDER_STATUSES.find(s => s.value === order.fulfillment_status) || ORDER_STATUSES[0];
                      const StatusIcon = currentStatus.icon;
                      return (
                        <Card 
                          key={order.id} 
                          className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/80 hover:border-orange-500/50 transition-colors cursor-pointer"
                          onClick={() => handleViewOrderDetail(order)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-bold text-orange-400">#{order.order_number || order.id?.slice(-8)}</h4>
                                  <Badge className={`${currentStatus.color} text-white text-xs flex items-center gap-1`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {currentStatus.label}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-300">{order.customer_name || order.customer_email}</p>
                                <p className="text-xs text-slate-400">{order.customer_email}</p>
                                <div className="mt-2 text-xs text-slate-400">
                                  <span className="font-medium text-green-400">${(order.total || 0).toFixed(2)}</span>
                                  {' • '}{order.items?.length || 0} items
                                  {' • '}{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                                </div>
                                {order.items && order.items.length > 0 && (
                                  <div className="mt-2 text-xs text-slate-500">
                                    Items: {order.items.map(item => `${item.product_name || item.name || 'Product'} (x${item.quantity})`).join(', ')}
                                  </div>
                                )}
                                <p className="text-xs text-purple-400 mt-2">Click to view full details →</p>
                              </div>
                              <div className="flex flex-col gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Select 
                                  value={order.fulfillment_status || 'ordered'} 
                                  onValueChange={(newStatus) => updateOrderStatus(order.id, newStatus)}
                                >
                                  <SelectTrigger className="w-[130px] bg-slate-700 border-slate-600 text-white text-xs h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-slate-800 border-slate-600">
                                    {ORDER_STATUSES.map(status => {
                                      const Icon = status.icon;
                                      return (
                                        <SelectItem key={status.value} value={status.value}>
                                          <div className="flex items-center gap-2">
                                            <Icon className="h-3 w-3" />
                                            {status.label}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers" className="flex-1 min-h-0 flex flex-col">
              {/* Show customer orders panel or customer list */}
              {viewingCustomerOrders ? (
                <>
                  {/* Customer Orders View */}
                  <div className="flex items-center gap-4 mb-4 flex-shrink-0">
                    <Button 
                      onClick={() => setViewingCustomerOrders(null)} 
                      variant="ghost" 
                      className="text-slate-300 hover:text-white"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Customers
                    </Button>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-white">
                        Orders for {viewingCustomerOrders.first_name} {viewingCustomerOrders.last_name}
                      </h3>
                      <p className="text-sm text-slate-400">{viewingCustomerOrders.email}</p>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto" style={{ minHeight: '200px' }}>
                    {customerOrdersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                      </div>
                    ) : customerOrders.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No orders found for this customer</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {customerOrders.map((order) => {
                          const currentStatus = ORDER_STATUSES.find(s => s.value === order.fulfillment_status) || ORDER_STATUSES[0];
                          const StatusIcon = currentStatus.icon;
                          return (
                            <Card 
                              key={order.id} 
                              className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/80 transition-colors cursor-pointer"
                              onClick={() => handleViewOrderDetail(order)}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-2">
                                      <h4 className="font-bold text-white text-lg">#{order.order_number || order.id?.slice(-8)}</h4>
                                      <Badge className={`${currentStatus.color} text-white text-xs flex items-center gap-1`}>
                                        <StatusIcon className="h-3 w-3" />
                                        {currentStatus.label}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="font-medium text-green-400">${(order.total || 0).toFixed(2)}</span>
                                      <span className="text-slate-400">{order.items?.length || 0} items</span>
                                      <span className="text-slate-400">
                                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                                      </span>
                                    </div>
                                    {order.items && order.items.length > 0 && (
                                      <div className="mt-2 text-xs text-slate-500 truncate">
                                        {order.items.map(item => item.product_name || item.name).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Select 
                                      value={order.fulfillment_status || 'ordered'} 
                                      onValueChange={(newStatus) => {
                                        event?.stopPropagation();
                                        updateOrderStatus(order.id, newStatus);
                                      }}
                                    >
                                      <SelectTrigger 
                                        className="w-[120px] bg-slate-700 border-slate-600 text-white text-xs h-8"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="bg-slate-800 border-slate-600">
                                        {ORDER_STATUSES.map(status => {
                                          const Icon = status.icon;
                                          return (
                                            <SelectItem key={status.value} value={status.value}>
                                              <div className="flex items-center gap-2">
                                                <Icon className="h-3 w-3" />
                                                {status.label}
                                              </div>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-orange-400 hover:text-orange-300"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewOrderDetail(order);
                                      }}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Customer List */}
                  <div className="flex gap-4 mb-4 flex-shrink-0">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search customers by name or email..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <Button onClick={fetchCustomers} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                      <RefreshCw className={`h-4 w-4 ${customersLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto" style={{ minHeight: '200px' }}>
                    {customersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                      </div>
                    ) : customers.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No customers found</p>
                        <p className="text-sm mt-1">Customers will appear here after they register</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {customers.map((customer) => (
                          <Card key={customer.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/80 transition-colors">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-white">
                                    {customer.first_name} {customer.last_name}
                                  </h4>
                                  <p className="text-sm text-slate-400 truncate">{customer.email}</p>
                                  {customer.phone && (
                                    <p className="text-xs text-slate-500">{customer.phone}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Button
                                    onClick={() => handleViewCustomerOrders(customer)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-orange-400 hover:text-orange-300 hover:bg-orange-900/30"
                                    title="View Orders"
                                  >
                                    <ShoppingCart className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setEditingCustomer({ ...customer });
                                      setShowCustomerDialog(true);
                                    }}
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                                    title="Edit Customer"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteCustomer(customer.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                    title="Delete Customer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-3 text-xs">
                                <button 
                                  onClick={() => handleViewCustomerOrders(customer)}
                                  className="text-slate-400 hover:text-orange-400 transition-colors"
                                >
                                  <span className="font-medium text-white">{customer.order_count || 0}</span> orders
                                </button>
                                <div className="text-slate-400">
                                  <span className="font-medium text-green-400">${(customer.total_spent || 0).toFixed(2)}</span> spent
                                </div>
                              </div>
                              {customer.addresses?.length > 0 && (
                                <div className="text-xs text-slate-500 mt-2">
                                  {customer.addresses.length} saved address(es)
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Settings Tab (formerly Import) */}
            <TabsContent value="settings" className="flex-1 min-h-0 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Store Settings Card */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Settings className="h-5 w-5 text-purple-400" />
                      Store Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Tax Rate Setting */}
                    <div>
                      <Label className="text-slate-300 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Tax Rate (%)
                      </Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Applied to all store orders. Current: {taxRate}%
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          max="100"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          className="bg-slate-700 border-slate-600 text-white w-32"
                        />
                        <span className="text-slate-400 self-center">%</span>
                        <Button 
                          onClick={saveTaxRate} 
                          disabled={savingTaxRate}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {savingTaxRate ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Default: 8.265% (Nevada sales tax)
                      </p>
                    </div>
                    
                    <Separator className="bg-slate-700" />
                    
                    {/* Shipping Settings Info */}
                    <div>
                      <Label className="text-slate-300 flex items-center gap-2">
                        <Truck className="h-4 w-4" />
                        Shipping Settings
                      </Label>
                      <div className="mt-2 p-3 bg-slate-900/50 rounded-lg text-sm">
                        <p className="text-slate-400"><span className="text-white">Calculated:</span> $5.99 base + $0.75/lb</p>
                        <p className="text-slate-400"><span className="text-white">Minimum:</span> $15.00</p>
                        <p className="text-slate-400"><span className="text-white">Local Pickup:</span> Free</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Static Pages Card */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-5 w-5 text-green-400" />
                      Legal Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-slate-400 text-sm">
                      Edit your Terms and Conditions and Privacy Policy pages.
                    </p>
                    
                    {pages.map((page) => (
                      <div key={page.slug} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-white">{page.title}</h4>
                          <p className="text-xs text-slate-400">
                            {page.is_default ? 'Using default content' : `Updated: ${new Date(page.updated_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-600 text-slate-300 hover:bg-slate-600"
                            onClick={() => window.open(`/${page.slug}`, '_blank')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setEditingPage(page);
                              setShowPageEditor(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <p className="text-xs text-slate-500">
                      Pages are shown at /terms and /privacy URLs.
                    </p>
                  </CardContent>
                </Card>

                {/* WooCommerce Import Card */}
                <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Upload className="h-5 w-5 text-orange-400" />
                      WooCommerce Import
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-slate-400">
                      Import products and categories from your WooCommerce store. Only published/active products will be imported.
                    </p>
                    
                    {importStatus && (
                      <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 max-w-xl">
                        <div className="flex items-center gap-2 mb-2">
                          {importStatus.status === 'completed' ? (
                            <CheckCircle className="h-5 w-5 text-green-400" />
                          ) : importStatus.status === 'in_progress' ? (
                            <Loader2 className="h-5 w-5 text-orange-400 animate-spin" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-yellow-400" />
                          )}
                          <span className="font-medium text-white capitalize">
                            {importStatus.status?.replace('_', ' ')}
                          </span>
                        </div>
                        
                        {importStatus.current_step && (
                          <p className="text-sm text-slate-400 mb-2">{importStatus.current_step}</p>
                        )}
                        
                        {importStatus.stats && (
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="text-slate-400">Products imported:</div>
                            <div className="text-white font-medium">{importStatus.stats.products_imported || 0}</div>
                            <div className="text-slate-400">Categories imported:</div>
                            <div className="text-white font-medium">{importStatus.stats.categories_imported || 0}</div>
                          </div>
                        )}
                        
                        {importStatus.completed_at && (
                          <p className="text-xs text-slate-500 mt-2">
                            Completed: {new Date(importStatus.completed_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <Button 
                      onClick={handleStartImport} 
                      disabled={importing}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Start Import
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-slate-500">
                      Note: Existing products with matching WooCommerce IDs will be skipped (not duplicated).
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Product Edit Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProduct?.id ? 'Edit Product' : 'New Product'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingProduct?.id ? 'Update product details' : 'Create a new product'}
            </DialogDescription>
          </DialogHeader>
          
          {editingProduct && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-300">Name *</Label>
                  <Input
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Product name"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">SKU</Label>
                  <Input
                    value={editingProduct.sku || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, sku: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="SKU-001"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Slug</Label>
                  <Input
                    value={editingProduct.slug || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, slug: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="product-slug"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Price *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingProduct.price || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Compare at Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingProduct.compare_at_price || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, compare_at_price: parseFloat(e.target.value) || null})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Original price (optional)"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Status</Label>
                  <Select 
                    value={editingProduct.status} 
                    onValueChange={(v) => setEditingProduct({...editingProduct, status: v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Product Type</Label>
                  <Select 
                    value={editingProduct.product_type} 
                    onValueChange={(v) => setEditingProduct({...editingProduct, product_type: v})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="physical">Physical</SelectItem>
                      <SelectItem value="digital">Digital</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Categories Section */}
                <div className="col-span-2 border-t border-slate-700 pt-4">
                  <Label className="text-slate-300 flex items-center gap-2 mb-3">
                    <Layers className="h-4 w-4" />
                    Categories
                  </Label>
                  
                  {/* Selected categories */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(editingProduct.category_ids || []).map((catId) => {
                      const cat = categories.find(c => c.id === catId);
                      if (!cat) return null;
                      return (
                        <Badge 
                          key={catId} 
                          className="bg-orange-600/20 text-orange-300 border border-orange-600/30 flex items-center gap-1"
                        >
                          {cat.name}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProduct({
                                ...editingProduct,
                                category_ids: editingProduct.category_ids.filter(id => id !== catId)
                              });
                            }}
                            className="ml-1 hover:text-red-400"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                    {(!editingProduct.category_ids || editingProduct.category_ids.length === 0) && (
                      <span className="text-sm text-slate-500">No categories selected</span>
                    )}
                  </div>
                  
                  {/* Category selector */}
                  <Select
                    value=""
                    onValueChange={(catId) => {
                      if (!editingProduct.category_ids?.includes(catId)) {
                        setEditingProduct({
                          ...editingProduct,
                          category_ids: [...(editingProduct.category_ids || []), catId]
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                      <SelectValue placeholder="Add a category..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600 max-h-60">
                      {categories
                        .filter(cat => !editingProduct.category_ids?.includes(cat.id))
                        .map((cat) => {
                          // Show hierarchy for subcategories
                          const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
                          const displayName = parent ? `${parent.name} → ${cat.name}` : cat.name;
                          return (
                            <SelectItem key={cat.id} value={cat.id}>
                              {displayName}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label className="text-slate-300">Short Description</Label>
                  <Textarea
                    value={editingProduct.short_description || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, short_description: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Brief product description"
                    rows={2}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-slate-300">Full Description</Label>
                  <Textarea
                    value={editingProduct.description || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Full product description (HTML supported)"
                    rows={4}
                  />
                </div>
                
                {/* Product Images */}
                <div className="col-span-2 border-t border-slate-700 pt-4">
                  <Label className="text-slate-300 flex items-center gap-2 mb-3">
                    <Image className="h-4 w-4" />
                    Product Images
                  </Label>
                  
                  {/* Current images */}
                  {editingProduct.images?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {editingProduct.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={img.url} 
                            alt={`Product ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded border border-slate-600"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = editingProduct.images.filter((_, i) => i !== idx);
                              setEditingProduct({...editingProduct, images: newImages});
                            }}
                            className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload new image */}
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const response = await axios.post(`${API}/api/store/upload/image`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          if (response.data.success) {
                            const newImage = {
                              url: `${API}${response.data.url}`,
                              alt: file.name
                            };
                            setEditingProduct({
                              ...editingProduct,
                              images: [...(editingProduct.images || []), newImage]
                            });
                            toast.success('Image uploaded');
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to upload image');
                        }
                        e.target.value = ''; // Reset input
                      }}
                      className="bg-slate-800 border-slate-600 text-white flex-1"
                    />
                  </div>
                  
                  {/* Or add by URL */}
                  <div className="mt-2">
                    <Input
                      placeholder="Or paste image URL..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value) {
                          const newImage = { url: e.target.value, alt: 'Product image' };
                          setEditingProduct({
                            ...editingProduct,
                            images: [...(editingProduct.images || []), newImage]
                          });
                          e.target.value = '';
                        }
                      }}
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Press Enter to add URL</p>
                  </div>
                </div>
                
                {/* Inventory */}
                <div className="col-span-2 border-t border-slate-700 pt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Checkbox
                      id="track_inventory"
                      checked={editingProduct.track_inventory}
                      onCheckedChange={(checked) => setEditingProduct({...editingProduct, track_inventory: checked})}
                    />
                    <Label htmlFor="track_inventory" className="text-slate-300 cursor-pointer">
                      Track inventory quantity
                    </Label>
                  </div>
                  
                  {editingProduct.track_inventory && (
                    <div>
                      <Label className="text-slate-300">Stock Quantity</Label>
                      <Input
                        type="number"
                        value={editingProduct.stock_quantity || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, stock_quantity: parseInt(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-600 text-white w-32"
                      />
                    </div>
                  )}
                </div>
                
                {/* Digital product options */}
                {editingProduct.product_type === 'digital' && (
                  <div className="col-span-2 border-t border-slate-700 pt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Checkbox
                        id="downloadable"
                        checked={editingProduct.downloadable}
                        onCheckedChange={(checked) => setEditingProduct({...editingProduct, downloadable: checked})}
                      />
                      <Label htmlFor="downloadable" className="text-slate-300 cursor-pointer">
                        Downloadable product
                      </Label>
                    </div>
                    
                    {editingProduct.downloadable && (
                      <div>
                        <Label className="text-slate-300">Download URL</Label>
                        <Input
                          value={editingProduct.download_files?.[0]?.url || ''}
                          onChange={(e) => setEditingProduct({
                            ...editingProduct, 
                            download_files: [{ name: 'Download', url: e.target.value }]
                          })}
                          className="bg-slate-800 border-slate-600 text-white"
                          placeholder="https://example.com/file.pdf"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleSaveProduct} disabled={savingProduct} className="bg-orange-600 hover:bg-orange-700">
              {savingProduct ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingProduct?.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Edit Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingCategory?.id ? 'Edit Category' : 'New Category'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingCategory?.id ? 'Update category details' : 'Create a new category'}
            </DialogDescription>
          </DialogHeader>
          
          {editingCategory && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-300">Name *</Label>
                <Input
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Category name"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Slug</Label>
                <Input
                  value={editingCategory.slug || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, slug: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="category-slug (auto-generated if empty)"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={editingCategory.description || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Category description"
                  rows={3}
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Category Image</Label>
                
                {/* Image Preview */}
                {editingCategory.image_url && (
                  <div className="relative w-32 h-32 mb-3 group">
                    <img 
                      src={editingCategory.image_url} 
                      alt="Category preview" 
                      className="w-full h-full object-cover rounded-lg border border-slate-600"
                    />
                    <button
                      onClick={() => setEditingCategory({...editingCategory, image_url: ''})}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                
                {/* Upload Image */}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      const response = await axios.post(`${API}/api/store/upload/image`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      if (response.data.success) {
                        setEditingCategory({
                          ...editingCategory,
                          image_url: `${API}${response.data.url}`
                        });
                        toast.success('Image uploaded');
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to upload image');
                    }
                    e.target.value = '';
                  }}
                  className="bg-slate-800 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Upload an image or paste a URL below</p>
                
                {/* Or paste URL */}
                <Input
                  value={editingCategory.image_url || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, image_url: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white mt-2"
                  placeholder="Or paste image URL..."
                />
              </div>
              
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="category_hidden"
                  checked={editingCategory.is_hidden || false}
                  onCheckedChange={(checked) => setEditingCategory({...editingCategory, is_hidden: checked})}
                />
                <Label htmlFor="category_hidden" className="text-slate-300 cursor-pointer flex items-center gap-2">
                  <EyeOff className="h-4 w-4" />
                  Hide this category from store
                </Label>
              </div>
              <p className="text-xs text-slate-500">Hidden categories and their products won&apos;t appear in the public store</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={savingCategory} className="bg-orange-600 hover:bg-orange-700">
              {savingCategory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCategory?.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Edit Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Customer</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update customer information
            </DialogDescription>
          </DialogHeader>
          
          {editingCustomer && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">First Name</Label>
                  <Input
                    value={editingCustomer.first_name || ''}
                    onChange={(e) => setEditingCustomer({...editingCustomer, first_name: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Last Name</Label>
                  <Input
                    value={editingCustomer.last_name || ''}
                    onChange={(e) => setEditingCustomer({...editingCustomer, last_name: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={editingCustomer.email || ''}
                  onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={editingCustomer.phone || ''}
                  onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              <div className="pt-2 border-t border-slate-700">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Account Stats</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Orders:</span>
                    <span className="text-white ml-2">{editingCustomer.order_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Total Spent:</span>
                    <span className="text-green-400 ml-2">${(editingCustomer.total_spent || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleSaveCustomer} disabled={savingCustomer} className="bg-orange-600 hover:bg-orange-700">
              {savingCustomer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={showOrderDetailDialog} onOpenChange={setShowOrderDetailDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              <FileText className="h-5 w-5 text-orange-400" />
              Order #{selectedOrder?.order_number || selectedOrder?.id?.slice(-8)}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedOrder?.created_at && `Placed on ${new Date(selectedOrder.created_at).toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 py-4">
              {/* Order Status */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <span className="text-slate-300">Fulfillment Status:</span>
                <div className="flex items-center gap-2">
                  {(() => {
                    const status = ORDER_STATUSES.find(s => s.value === selectedOrder.fulfillment_status) || ORDER_STATUSES[0];
                    const StatusIcon = status.icon;
                    return (
                      <Badge className={`${status.color} text-white flex items-center gap-1`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                    );
                  })()}
                  <Select 
                    value={selectedOrder.fulfillment_status || 'ordered'} 
                    onValueChange={(newStatus) => {
                      updateOrderStatus(selectedOrder.id, newStatus);
                      setSelectedOrder({...selectedOrder, fulfillment_status: newStatus});
                    }}
                  >
                    <SelectTrigger className="w-[120px] bg-slate-700 border-slate-600 text-white text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {ORDER_STATUSES.map(status => {
                        const Icon = status.icon;
                        return (
                          <SelectItem key={status.value} value={status.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-3 w-3" />
                              {status.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Customer Info - Enhanced */}
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2 border-b border-slate-700 pb-2">
                  <Users className="h-4 w-4" />
                  Customer Information
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <span className="text-slate-500">Name:</span>
                    <p className="text-white font-medium">{selectedOrder.customer_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <p className="text-white">{selectedOrder.customer_email || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone:</span>
                    <p className="text-white">
                      {selectedOrder.shipping_address?.phone || selectedOrder.customer_phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Customer ID:</span>
                    <p className="text-slate-400 font-mono text-xs">{selectedOrder.customer_id?.slice(0, 8) || 'N/A'}...</p>
                  </div>
                </div>
              </div>

              {/* Shipping Address - Enhanced */}
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2 border-b border-slate-700 pb-2">
                  <Truck className="h-4 w-4" />
                  Shipping Details
                </h4>
                {selectedOrder.shipping_address ? (
                  <div className="space-y-2">
                    <div className="text-sm">
                      <p className="text-white font-medium">
                        {selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}
                      </p>
                      <p className="text-slate-300">
                        {selectedOrder.shipping_address.address_line1 || selectedOrder.shipping_address.address_1}
                      </p>
                      {(selectedOrder.shipping_address.address_line2 || selectedOrder.shipping_address.address_2) && (
                        <p className="text-slate-300">
                          {selectedOrder.shipping_address.address_line2 || selectedOrder.shipping_address.address_2}
                        </p>
                      )}
                      <p className="text-slate-300">
                        {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postal_code || selectedOrder.shipping_address.postcode}
                      </p>
                      <p className="text-slate-400">
                        {selectedOrder.shipping_address.country || 'US'}
                      </p>
                    </div>
                    <div className="pt-2 border-t border-slate-700 flex items-center gap-4 text-sm">
                      <span className="text-slate-500">Method:</span>
                      <Badge variant="outline" className="border-slate-600 text-slate-300">
                        {(selectedOrder.shipping_method || 'N/A').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No shipping address provided</p>
                )}
              </div>

              {/* Order Items */}
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2 border-b border-slate-700 pb-2">
                  <Package className="h-4 w-4" />
                  Order Items ({selectedOrder.items?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-700 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-700 rounded overflow-hidden flex-shrink-0">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.product_name || item.name || 'Product'} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-5 w-5 text-slate-500" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{item.product_name || item.name || 'Product'}</p>
                          {item.variant_name && (
                            <p className="text-xs text-purple-400">{item.variant_name}</p>
                          )}
                          {item.sku && (
                            <p className="text-xs text-slate-500">SKU: {item.sku}</p>
                          )}
                          <p className="text-xs text-slate-400">
                            ${(item.price || 0).toFixed(2)} × {item.quantity}
                          </p>
                        </div>
                      </div>
                      <p className="text-white font-bold">
                        ${(item.line_total || (item.price || 0) * (item.quantity || 1)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                  {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                    <p className="text-slate-500 text-sm text-center py-4">No item details available for this order</p>
                  )}
                </div>
              </div>

              {/* Order Totals - Enhanced */}
              <div className="p-3 bg-green-900/20 rounded-lg border border-green-800/50">
                <h4 className="text-sm font-medium text-green-300 mb-3 flex items-center gap-2 border-b border-green-800/50 pb-2">
                  <DollarSign className="h-4 w-4" />
                  Payment Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Subtotal:</span>
                    <span className="text-white">${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Shipping ({(selectedOrder.shipping_method || '').replace(/_/g, ' ')}):</span>
                    <span className="text-white">${(selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tax (8.265%):</span>
                    <span className="text-white">${(selectedOrder.tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-green-800/50 mt-2">
                    <span className="text-white font-bold text-lg">Total Paid:</span>
                    <span className="text-green-400 font-bold text-xl">${(selectedOrder.total || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-green-800/50 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Payment Status:</span>
                  <Badge className="bg-green-600 text-white">{selectedOrder.payment_status || 'paid'}</Badge>
                </div>
              </div>

              {/* Payment Info */}
              {selectedOrder.payment_intent_id && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Stripe Payment Details</h4>
                  <p className="text-xs text-slate-400 break-all">
                    Payment ID: <span className="text-slate-300 font-mono">{selectedOrder.payment_intent_id}</span>
                  </p>
                </div>
              )}

              {/* Fulfillment History */}
              {selectedOrder.fulfillment_history?.length > 0 && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Status History</h4>
                  <div className="space-y-1 text-xs">
                    {selectedOrder.fulfillment_history.map((entry, idx) => (
                      <div key={idx} className="flex justify-between text-slate-400">
                        <span className="flex items-center gap-1">
                          {entry.previous_status && <span className="text-slate-500">{entry.previous_status}</span>}
                          {entry.previous_status && <span>→</span>}
                          <span className="text-white">{entry.status}</span>
                        </span>
                        <span>{new Date(entry.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tracking Info */}
              {selectedOrder.tracking_number && (
                <div className="p-3 bg-purple-900/30 rounded-lg border border-purple-700">
                  <h4 className="text-sm font-medium text-purple-300 mb-2 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Tracking Information
                  </h4>
                  <p className="text-lg font-mono text-white">{selectedOrder.tracking_number}</p>
                  {selectedOrder.tracking_carrier && (
                    <p className="text-xs text-slate-400 mt-1">Carrier: {selectedOrder.tracking_carrier}</p>
                  )}
                  {selectedOrder.shipped_at && (
                    <p className="text-xs text-slate-500 mt-1">Shipped: {new Date(selectedOrder.shipped_at).toLocaleString()}</p>
                  )}
                </div>
              )}

              {/* Order Notes */}
              {selectedOrder.notes && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Order Notes</h4>
                  <p className="text-sm text-slate-400">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDetailDialog(false)} className="border-slate-600">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tracking Number Dialog */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-400" />
              Ship Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter the tracking information for order #{pendingShipOrder?.order?.order_number || pendingShipOrder?.orderId?.slice(-8)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Tracking Number</Label>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Carrier (Optional)</Label>
              <Select value={trackingCarrier} onValueChange={setTrackingCarrier}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="usps">USPS</SelectItem>
                  <SelectItem value="ups">UPS</SelectItem>
                  <SelectItem value="fedex">FedEx</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 text-sm text-blue-300">
              <p>📧 A shipping notification email with tracking info will be sent to the customer.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTrackingDialog(false);
                setPendingShipOrder(null);
              }} 
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleShipWithTracking}
              className="bg-green-600 hover:bg-green-700"
            >
              <Truck className="h-4 w-4 mr-2" />
              Ship Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Editor Dialog */}
      <Dialog open={showPageEditor} onOpenChange={setShowPageEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-400" />
              Edit {editingPage?.title}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Edit the HTML content for this page. Use standard HTML tags for formatting.
            </DialogDescription>
          </DialogHeader>
          
          {editingPage && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              <div>
                <Label className="text-slate-300">Page Title</Label>
                <Input
                  value={editingPage.title}
                  onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex-1">
                <Label className="text-slate-300">Content (HTML)</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Use HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href=&quot;&quot;&gt;
                </p>
                <Textarea
                  value={editingPage.content}
                  onChange={(e) => setEditingPage({...editingPage, content: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white font-mono text-sm min-h-[400px]"
                  placeholder="<h2>Section Title</h2>
<p>Your content here...</p>"
                />
              </div>
              
              {/* Preview */}
              <div>
                <Label className="text-slate-300 mb-2 block">Preview</Label>
                <div className="bg-slate-800 border border-slate-600 rounded-lg p-4 max-h-[200px] overflow-y-auto">
                  <div 
                    className="prose prose-invert prose-sm max-w-none
                      prose-headings:text-white prose-p:text-slate-300
                      prose-li:text-slate-300 prose-strong:text-white
                      prose-a:text-green-400"
                    dangerouslySetInnerHTML={{ __html: editingPage.content }}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowPageEditor(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={savePage}
              disabled={savingPage}
              className="bg-green-600 hover:bg-green-700"
            >
              {savingPage ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Save Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StoreManager;
